import { useContext, useState } from "react";
import AuthContext from "@/store/contexts/AuthContext";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Plus, Search } from "lucide-react";
import { useTranslation } from "react-i18next";
import axios from "@/services/axios";
import { toast } from "sonner";
import { Category } from "@/type/common";

interface PageCategorySelectorProps {
  value?: number | null;
  onChange: (value: number | null) => void;
}

const PageCategorySelector = ({
  value,
  onChange,
}: PageCategorySelectorProps) => {
  const { categories, loadCategories } = useContext(AuthContext);
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");

  const selectedCategory = categories.find((cat) => cat.id === value);

  // Filter categories based on search
  const filteredCategories = searchQuery
    ? categories.filter((cat) =>
        cat.name.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : categories;

  const handleValueChange = (selectedValue: string) => {
    if (selectedValue === "none") {
      onChange(null);
    } else if (selectedValue === "create") {
      setIsCreating(true);
    } else {
      onChange(parseInt(selectedValue));
    }
  };

  const handleCreateCategory = async () => {
    if (!newCategoryName.trim()) {
      toast.error(t("home.categoryNameRequired"));
      return;
    }

    try {
      // Create category via Odoo API
      const response = await axios.post("/odash/categories/create", {
        name: newCategoryName.trim(),
      });

      if (response.data) {
        const newCategory: Category = response.data;
        toast.success(t("home.categoryCreated"));
        
        // Reload categories to get the new one
        await loadCategories();
        
        // Set the newly created category as selected
        onChange(newCategory.id);
        
        // Reset state
        setIsCreating(false);
        setNewCategoryName("");
        setSearchQuery("");
      }
    } catch (error) {
      console.error("Error creating category:", error);
      toast.error(t("home.categoryCreateError"));
    }
  };

  const handleCancelCreate = () => {
    setIsCreating(false);
    setNewCategoryName("");
  };

  if (isCreating) {
    return (
      <div className="space-y-2">
        <Label>{t("home.newCategory")}</Label>
        <div className="space-y-2">
          <Input
            placeholder={t("home.categoryNamePlaceholder")}
            value={newCategoryName}
            onChange={(e) => setNewCategoryName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleCreateCategory();
              } else if (e.key === "Escape") {
                handleCancelCreate();
              }
            }}
            autoFocus
          />
          <div className="flex gap-2">
            <Button
              size="sm"
              onClick={handleCreateCategory}
              disabled={!newCategoryName.trim()}
            >
              {t("home.create")}
            </Button>
            <Button size="sm" variant="outline" onClick={handleCancelCreate}>
              {t("home.cancel")}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <Label>{t("home.category")}</Label>
      <Select
        value={value ? value.toString() : "none"}
        onValueChange={handleValueChange}
      >
        <SelectTrigger className="w-64">
          <SelectValue placeholder={t("home.selectCategoryPlaceholder")}>
            {value && selectedCategory ? selectedCategory.name : t("home.noCategory")}
          </SelectValue>
        </SelectTrigger>
        <SelectContent className="max-h-96 overflow-hidden">
          {/* Search Input */}
          <div className="px-2 py-2 sticky top-0 bg-background z-10 border-b">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={t("home.searchCategories")}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-8 pl-8"
              />
            </div>
          </div>

          <div className="overflow-y-auto max-h-80 px-1 py-1">
            {/* Create New Category Button */}
            <SelectItem value="create" className="flex items-center gap-2 font-medium text-primary">
              <span className="flex items-center gap-2">
                <Plus className="h-4 w-4" />
                {t("home.createNewCategory")}
              </span>
            </SelectItem>

            {/* No Category Option */}
            <SelectItem value="none">{t("home.noCategory")}</SelectItem>

            {/* Filtered Categories */}
            {filteredCategories.length > 0 ? (
              filteredCategories.map((category) => (
                <SelectItem key={category.id} value={category.id.toString()}>
                  {category.name}
                </SelectItem>
              ))
            ) : (
              <div className="px-2 py-4 text-sm text-muted-foreground text-center">
                {t("home.noCategoriesFound")}
              </div>
            )}
          </div>
        </SelectContent>
      </Select>
    </div>
  );
};

export default PageCategorySelector;
